import { CheckIcon, CloseIcon, LockIcon } from "@chakra-ui/icons";
import { IconButtonProps } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useDeleteVideo from "../../hooks/api/video/useDeleteVideo";
import useGetVideosPaginated from "../../hooks/api/video/useGetVideosPaginated";
import useUpdateVideoStatus from "../../hooks/api/video/useUpdateVideoStatus";
import { Video } from "../../models/Video";
import useAuthState from "../../state/auth.state";
import GenericTable from "./GenericTable";

export default function VideosTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useAuthState();
  const {
    data: videos,
    isPending,
    isError,
    mutateAsync: fetchVideos,
    variables,
  } = useGetVideosPaginated();
  const {
    mutateAsync: updateVideoStatus,
    isPending: isUpdateVideoStatusLoading,
    variables: updateVideoStatusVariables,
  } = useUpdateVideoStatus();
  const { mutateAsync: deleteVideo, isPending: isDeletionPending } =
    useDeleteVideo();

  const onDeleteConfirm = useCallback(
    (video: Video) => {
      deleteVideo({ videoId: video.id! }).finally(() =>
        fetchVideos({ page: variables?.page ?? 0 }),
      );
    },
    [deleteVideo, fetchVideos, variables?.page],
  );

  const extraActions: (video: Video) => IconButtonProps[] = useCallback(
    (video) => {
      const actions: IconButtonProps[] = [];

      const doUpdate = (status: Video["status"]) =>
        updateVideoStatus({
          videoId: video.id!,
          status,
        }).finally(() => fetchVideos({ page: variables?.page ?? 0 }));

      if (role === "ROLE_ADMIN") {
        if (video.status === "PENDING") {
          actions.push({
            onClick: () => doUpdate("APPROVED"),
            bg: "green.100",
            color: "gren.800",
            "aria-label": t("common.buttons.approve"),
            isLoading:
              isUpdateVideoStatusLoading &&
              updateVideoStatusVariables?.videoId === video.id &&
              updateVideoStatusVariables?.status === "APPROVED",
            icon: <CheckIcon />,
          });
          actions.push({
            onClick: () => doUpdate("REJECTED"),
            bg: "red.100",
            color: "red.800",
            "aria-label": t("common.buttons.reject"),
            isLoading:
              isUpdateVideoStatusLoading &&
              updateVideoStatusVariables?.videoId === video.id &&
              updateVideoStatusVariables?.status === "REJECTED",
            icon: <CloseIcon />,
          });
        }

        if (video.status === "APPROVED") {
          actions.push({
            onClick: () => doUpdate("DISABLED"),
            bg: "orange.100",
            color: "orange.800",
            "aria-label": t("common.buttons.disable"),
            isLoading:
              isUpdateVideoStatusLoading &&
              updateVideoStatusVariables?.videoId === video.id &&
              updateVideoStatusVariables?.status === "DISABLED",
            icon: <LockIcon />,
          });
        }
      }

      return actions;
    },
    [
      fetchVideos,
      isUpdateVideoStatusLoading,
      role,
      t,
      updateVideoStatus,
      updateVideoStatusVariables?.status,
      updateVideoStatusVariables?.videoId,
      variables?.page,
    ],
  );

  return (
    <GenericTable<Video>
      items={videos?.content ?? []}
      headers={[
        t("tables.name"),
        t("tables.description"),
        t("tables.uploaded_by"),
        t("tables.status"),
      ]}
      rowExtractors={[
        (video) => video.title,
        (video) => video.description,
        (video) => video.createdBy.fullName,
        (video) => {
          switch (video.status) {
            case "PENDING":
              return t("common.labels.pending");
            case "APPROVED":
              return t("common.labels.approved");
            case "REJECTED":
              return t("common.labels.rejected");
            default:
              return t("common.labels.disabled");
          }
        },
      ]}
      onEditClick={(video) => navigate(`./${video.id}`)}
      onDeleteConfirm={onDeleteConfirm}
      isDeletionPending={isDeletionPending}
      isLoading={isPending}
      isError={isError}
      pagination={videos?.page}
      refetch={fetchVideos}
      extraActions={extraActions}
    />
  );
}
